import axios from "axios";
import jsPDF from "jspdf";

const IOT_BASE = "https://api.livestockify.com/api/chennai/iot";

function buildIotReportUrl(deviceId, rangeType, startDate, endDate) {
  if (rangeType === "Today" || rangeType === "Daily") {
    return `${IOT_BASE}/daily?deviceId=${deviceId}`;
  }
  if (rangeType === "Weekly") {
    return `${IOT_BASE}/week?deviceId=${deviceId}`;
  }
  if (rangeType === "Monthly") {
    return `${IOT_BASE}/month?deviceId=${deviceId}`;
  }
  if (rangeType === "Custom" && startDate && endDate) {
    const start = `${startDate}T00:00:00`;
    const end = `${endDate}T23:59:59`;
    return `${IOT_BASE}/custom?deviceId=${deviceId}&start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`;
  }
  return null;
}

export async function downloadIotDeviceReportPdf({
  token,
  device,
  rangeType,
  startDate,
  endDate,
}) {
  if (!device) {
    alert("Please select a device before downloading the report.");
    return;
  }

  try {
    const url = buildIotReportUrl(device.deviceId, rangeType, startDate, endDate);
    if (!url) {
      alert("Please select a valid date range.");
      return;
    }

    const res = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!res.data || !Array.isArray(res.data) || res.data.length === 0) {
      alert("No data found for this device in the selected range.");
      return;
    }

    const deviceRecords = res.data;
    const totalRecords = deviceRecords.length;

    const pdf = new jsPDF("p", "mm", "a4");
    let y = 20;

    pdf.setFontSize(18);
    pdf.text("Device Data Report", 15, y);
    y += 10;

    pdf.setFontSize(12);
    pdf.text(`Device: ${device.name}`, 15, y);
    y += 6;
    pdf.text(`Device ID: ${device.deviceId}`, 15, y);
    y += 6;
    pdf.text(
      `Date Range: ${rangeType}${
        rangeType === "Custom" ? ` (${startDate} to ${endDate})` : ""
      }`,
      15,
      y,
    );
    y += 6;
    pdf.text(`Total Records: ${totalRecords}`, 15, y);
    y += 10;

    pdf.setFontSize(11);
    pdf.setFont("helvetica", "bold");
    pdf.text("Timestamp", 15, y);
    pdf.text("pH", 90, y);
    pdf.text("Temperature (°C)", 130, y);
    y += 6;
    pdf.line(15, y, 200, y);
    y += 6;

    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(0, 0, 0);

    deviceRecords.forEach((record) => {
      const timestamp = record.timestamp
        ? new Date(record.timestamp).toLocaleString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })
        : "-";
      const sensors = record.sensors || {};
      const phRaw = sensors.ph ?? sensors.pH ?? sensors.PH ?? null;
      const tempRaw = sensors.temperature ?? sensors.Temperature ?? null;
      const phValue = typeof phRaw === "number" ? phRaw.toFixed(3) : "-";
      const tempValue = typeof tempRaw === "number" ? tempRaw.toFixed(3) : "-";

      pdf.text(timestamp, 15, y);
      pdf.text(phValue, 90, y);
      pdf.text(tempValue, 130, y);
      y += 6;

      if (y > 270) {
        pdf.addPage();
        y = 20;
      }
    });

    pdf.setFontSize(10);
    pdf.setTextColor(100);
    pdf.text(
      "Auto-generated IoT Data Report - Chennai Dashboard",
      15,
      y > 280 ? 280 : y,
    );

    pdf.save(`${device.name}_Live_Data_Report.pdf`);
  } catch (error) {
    console.error("Error downloading report:", error);
    alert("Failed to generate PDF report. Please check console for details.");
  }
}

export async function downloadCrusherReportPdf({
  device,
  selectedRange,
  customStart,
  customEnd,
  crusherRangeTotals,
}) {
  if (!device) {
    alert("Please select a crusher device first.");
    return;
  }

  try {
    let type = "today";
    if (selectedRange === "Weekly") type = "weekly";
    else if (selectedRange === "Monthly") type = "monthly";
    else if (selectedRange === "Daily") type = "today";
    else if (selectedRange === "Custom") type = "custom";

    let url = `https://api.livestockify.com/api/crusher-devices/summary?deviceId=${device.deviceId}&type=${type}`;
    if (type === "custom" && customStart && customEnd) {
      url += `&startDate=${customStart}&endDate=${customEnd}`;
    }

    const res = await axios.get(url);
    if (!res.data || !res.data.graphData || res.data.graphData.length === 0) {
      alert("No crusher data found.");
      return;
    }

    const records = res.data.graphData;
    const pdf = new jsPDF("p", "mm", "a4");
    let y = 20;

    pdf.setFontSize(18);
    pdf.text("Crusher Device Report", 15, y);
    y += 10;

    pdf.setFontSize(12);
    pdf.text(`Device Name: ${device.name}`, 15, y);
    y += 6;
    pdf.text(`Device ID: ${device.deviceId}`, 15, y);
    y += 6;
    pdf.text(
      `Total Crush: ${crusherRangeTotals.totalCrush.toFixed(2)} Kg`,
      15,
      y,
    );
    y += 6;
    pdf.text(
      `Total On Time: ${crusherRangeTotals.totalOnTimeMinutes} Minutes`,
      15,
      y,
    );
    y += 10;

    pdf.setFont("helvetica", "bold");
    pdf.text("Timestamp", 15, y);
    pdf.text("Crush (Kg)", 110, y);
    pdf.text("On Time (Min)", 150, y);
    y += 5;
    pdf.line(15, y, 195, y);
    y += 6;

    pdf.setFont("helvetica", "normal");
    records.forEach((row) => {
      const time = new Date(row.timestamp).toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
      const crush = row.crush ?? 0;
      const ontime =
        row.ontime ?? row.onTime ?? row.totalOntime ?? (row.crush ? 10 : 0);

      pdf.text(time, 15, y);
      pdf.text(Number(crush).toFixed(2), 110, y);
      pdf.text(String(ontime), 150, y);
      y += 6;

      if (y > 270) {
        pdf.addPage();
        y = 20;
      }
    });

    pdf.save(`${device.name}_Crusher_Report.pdf`);
  } catch (error) {
    console.error("Crusher PDF error:", error);
    alert("Failed to generate crusher report.");
  }
}
